// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Re-entry Passes (Read-Only)
// Simple view: active/recent passes only, print certificate
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Modal,
  Code, CopyButton, ActionIcon, Stack, Divider, Alert,
} from '../../../components/gab/GabComponents';
import { useDisclosure } from '../../../components/gab/GabHooks';
import {
  IconRefresh, IconCopy, IconCheck, IconPrinter, IconShieldCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMainStreetOpsStore, type MSReentryPass } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { primary, semantic } from '../../../lib/colors';
import { fmtDate } from '../../../lib/dateFormat';

const STATUS_COLORS: Record<string, string> = {
  active: 'green', issued: 'blue', verified: 'teal', revoked: 'red', expired: 'gray',
};

function getVerifyUrl(code: string): string {
  return `${window.location.origin}/mainstreet/verify/${encodeURIComponent(code)}`;
}

export default function PortalReentryPasses() {
  const myOrg = useMyOrg();
  const { loading, loadReentryPasses, loadReentryAuths, reentryPasses, reentryAuths } = useMainStreetOpsStore();
  const activeIncident = useAuthStore(s => s.activeIncident);

  const [certPass, setCertPass] = useState<MSReentryPass | null>(null);
  const [certOpened, { open: openCert, close: closeCert }] = useDisclosure(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const certRef = useRef<HTMLDivElement>(null);

  // Find authorizations that apply to this org (by orgId, sector, or "all")
  const applicableAuthIds = useMemo(() => {
    if (!myOrg) return new Set<string>();
    const orgId = String(myOrg.id);
    const sector = myOrg.sectorType ?? '';

    const ids = new Set<string>();
    for (const auth of reentryAuths) {
      if (auth.targetType === 'all') {
        ids.add(String(auth.id));
        ids.add(String(auth.Rid));
      } else if (auth.targetType === 'sector' && sector && auth.targetSectors.split(',').includes(sector)) {
        ids.add(String(auth.id));
        ids.add(String(auth.Rid));
      } else if (auth.targetType === 'specific' && auth.targetOrgs.split(',').includes(orgId)) {
        ids.add(String(auth.id));
        ids.add(String(auth.Rid));
      }
    }
    return ids;
  }, [reentryAuths, myOrg]);

  // Passes: match by orgId directly OR by applicable authorization
  // Only show active/valid passes (not revoked, not expired)
  const activePasses = useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    const now = new Date();

    return reentryPasses
      .filter(p => {
        // Must belong to this org
        if (p.orgId !== orgId && !(p.authId && applicableAuthIds.has(p.authId))) return false;
        // Filter out revoked
        if (p.status === 'revoked') return false;
        // Filter out expired
        if (p.expiresAt && new Date(p.expiresAt) < now) return false;
        return true;
      })
      .sort((a, b) => (b.issuedAt ?? '').localeCompare(a.issuedAt ?? ''));
  }, [reentryPasses, myOrg, applicableAuthIds]);

  // Most recent pass (featured at top)
  const latestPass = activePasses[0] ?? null;

  const getAuthTitle = useCallback((authId: string) => {
    const auth = reentryAuths.find(a => String(a.Rid) === authId || String(a.id) === authId);
    return auth?.title ?? '';
  }, [reentryAuths]);

  const viewCertificate = useCallback(async (pass: MSReentryPass) => {
    setCertPass(pass);
    try {
      const url = getVerifyUrl(pass.code);
      const dataUrl = await QRCode.toDataURL(url, {
        width: 200, margin: 2,
        color: { dark: primary[900], light: '#ffffff' },
      });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl('');
    }
    openCert();
  }, [openCert]);

  function handlePrint() {
    const printContent = certRef.current;
    if (!printContent) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Re-entry Pass</title>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">
      <style>body{margin:0;padding:20px;font-family:'DM Sans',sans-serif}@media print{body{padding:0}}</style>
      </head><body>${printContent.innerHTML}</body></html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Re-entry Passes</Title>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          loading={loading} onClick={() => { loadReentryAuths(); loadReentryPasses(); }}>Refresh</Button>
      </Group>

      {/* No passes state */}
      {activePasses.length === 0 && !loading && (
        <Alert icon={<IconAlertCircle size={16} />} color="gray" variant="light">
          No active re-entry passes for your organization.
          Passes are issued by the EOC when re-entry is authorized for your area or sector.
        </Alert>
      )}

      {/* Featured: most recent active pass */}
      {latestPass && (
        <Paper withBorder p="lg" mb="md" style={{ borderLeft: '4px solid var(--mantine-color-green-6)' }}>
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <div>
              <Group gap="xs" mb={4}>
                <IconShieldCheck size={20} color="var(--mantine-color-green-6)" />
                <Text fw={700} size="lg">Active Re-entry Pass</Text>
              </Group>
              {getAuthTitle(latestPass.authId) && (
                <Text size="sm" c="dimmed" mb={4}>{getAuthTitle(latestPass.authId)}</Text>
              )}
              <Group gap="lg" mt="xs">
                <div>
                  <Text size="xs" c="dimmed">Pass Code</Text>
                  <Group gap={4}>
                    <Code style={{ fontFamily: 'DM Mono, monospace', letterSpacing: 2, fontSize: '1.1rem' }}>
                      {latestPass.code}
                    </Code>
                    <CopyButton value={latestPass.code}>
                      {({ copied, copy }) => (
                        <ActionIcon size="sm" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy}>
                          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        </ActionIcon>
                      )}
                    </CopyButton>
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Issued</Text>
                  <Text size="sm" fw={500}>{fmtDate(latestPass.issuedAt)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Expires</Text>
                  <Text size="sm" fw={500}>{fmtDate(latestPass.expiresAt)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge color="green" variant="filled" size="sm">{latestPass.status}</Badge>
                </div>
              </Group>
            </div>
            <Stack gap="xs">
              <Button size="sm" leftSection={<IconPrinter size={16} />}
                onClick={() => viewCertificate(latestPass)}>
                View / Print
              </Button>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Additional active passes (if more than 1) */}
      {activePasses.length > 1 && (
        <>
          <Text size="sm" fw={600} mb="xs" mt="md">
            All Active Passes ({activePasses.length})
          </Text>
          <Paper withBorder>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Code</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Authorization</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Issued</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Expires</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activePasses.map(pass => (
                    <tr key={pass.id}
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                      onClick={() => viewCertificate(pass)}>
                      <td style={{ padding: '8px 12px' }}>
                        <Code style={{ fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>
                          {pass.code}
                        </Code>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Text size="xs">{getAuthTitle(pass.authId) || '-'}</Text>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Badge size="xs" color={STATUS_COLORS[pass.status] ?? 'gray'}>{pass.status}</Badge>
                      </td>
                      <td style={{ padding: '8px 12px' }}>{fmtDate(pass.issuedAt)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmtDate(pass.expiresAt)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <Button size="compact-xs" variant="light" leftSection={<IconPrinter size={12} />}
                          onClick={(e) => { e.stopPropagation(); viewCertificate(pass); }}>
                          Print
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </>
      )}

      {/* Certificate Modal */}
      <Modal opened={certOpened} onClose={closeCert} title="Re-entry Pass Certificate" size="lg" centered>
        {certPass && (
          <Stack gap="md">
            <div ref={certRef} style={{
              background: '#fff', border: '3px solid var(--color-primary-800)', borderRadius: 12,
              padding: 32, maxWidth: 520, margin: '0 auto', fontFamily: "'DM Sans', sans-serif",
            }}>
              <div style={{ height: 6, background: 'linear-gradient(90deg, var(--color-primary-800), var(--color-primary-700), var(--color-primary-800))', borderRadius: 3, marginBottom: 24 }} />
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 13, letterSpacing: 3, color: primary[800], fontWeight: 700, marginBottom: 4 }}>
                  OPENGOV EMERGENCY MANAGEMENT
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: primary[900], letterSpacing: 1 }}>
                  MAINSTREET RE-ENTRY PASS
                </div>
              </div>
              {qrDataUrl && (
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <img src={qrDataUrl} alt="QR Code"
                    style={{ width: 180, height: 180, border: '1px solid #e0e0e0', borderRadius: 8 }} />
                </div>
              )}
              <div style={{ textAlign: 'center', marginBottom: 20, padding: '12px 0', background: '#f8f6ff', borderRadius: 8, border: '1px solid #e0d8ff' }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Pass Code</div>
                <div style={{ fontSize: 32, fontWeight: 800, fontFamily: "'DM Mono', monospace", letterSpacing: 4, color: primary[900] }}>
                  {certPass.code}
                </div>
              </div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                {(() => {
                  const isExpired = certPass.expiresAt ? new Date(certPass.expiresAt) < new Date() : false;
                  const isRevoked = certPass.status === 'revoked';
                  const isValid = !isExpired && !isRevoked;
                  return (
                    <span style={{
                      display: 'inline-block', padding: '6px 24px', borderRadius: 20,
                      fontSize: 14, fontWeight: 700, letterSpacing: 1, color: '#fff',
                      background: isValid ? '#07963F' : semantic.error,
                    }}>
                      {isRevoked ? 'REVOKED' : isExpired ? 'EXPIRED' : 'VALID'}
                    </span>
                  );
                })()}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20, fontSize: 13 }}>
                <div>
                  <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Organization</div>
                  <div style={{ fontWeight: 600, color: '#333' }}>{certPass.orgName || myOrg?.name || '-'}</div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Authorization</div>
                  <div style={{ fontWeight: 600, color: '#333' }}>{getAuthTitle(certPass.authId) || '-'}</div>
                </div>
                {activeIncident?.name && (
                  <div>
                    <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Incident</div>
                    <div style={{ fontWeight: 600, color: '#333' }}>{activeIncident.name}</div>
                  </div>
                )}
                <div>
                  <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Issued</div>
                  <div style={{ fontWeight: 600, color: '#333' }}>{fmtDate(certPass.issuedAt)}</div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Expires</div>
                  <div style={{ fontWeight: 600, color: certPass.expiresAt && new Date(certPass.expiresAt) < new Date() ? 'var(--color-error)' : '#333' }}>
                    {fmtDate(certPass.expiresAt)}
                  </div>
                </div>
              </div>
              <div style={{ height: 6, background: 'linear-gradient(90deg, var(--color-primary-800), var(--color-primary-700), var(--color-primary-800))', borderRadius: 3, marginTop: 24 }} />
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <div style={{ fontSize: 10, color: '#999', letterSpacing: 0.5 }}>
                  Verify at: {getVerifyUrl(certPass.code)}
                </div>
              </div>
            </div>

            <Divider />

            <Group justify="center" gap="sm">
              <Button variant="light" leftSection={<IconPrinter size={16} />} onClick={handlePrint}>
                Print
              </Button>
              <CopyButton value={certPass.code}>
                {({ copied, copy }) => (
                  <Button variant="light" color={copied ? 'green' : 'gray'}
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    onClick={copy}>
                    {copied ? 'Copied!' : 'Copy Code'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
